/** Vendor payout status — what admin / vendor still need to do (not customer booking status). */

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const VENDOR_PAYOUT_LEGEND_ADMIN = [
  { badge: 'success', label: 'Paid', desc: 'Payment recorded — nothing owed for this trip' },
  { badge: 'warning', label: 'Ready to pay', desc: 'Click Record payment — confirms trip and pays vendor in one step' },
  { badge: 'secondary', label: 'Cancelled', desc: 'Closed — no payout' },
];

export const VENDOR_PAYOUT_LEGEND_VENDOR = [
  { badge: 'success', label: 'Paid', desc: 'VisitAllNepal recorded your payment' },
  { badge: 'warning', label: 'Awaiting payment', desc: 'Trip done on our side — payment is being processed' },
  { badge: 'light', label: 'In progress', desc: 'Mark complete when you finish the trip' },
];

/**
 * @param {object} item — trip / vendor_service from API
 * @param {{ audience?: 'admin'|'vendor' }} options
 */
export function vendorPayoutStatus(item = {}, options = {}) {
  const audience = options.audience || 'admin';

  if (item.departure_status === 'cancelled' || item.admin_force_closed) {
    return { key: 'cancelled', label: 'Cancelled', badge: 'secondary', hint: 'No payout' };
  }

  const amount = vendorShareAmount(item).value ?? 0;
  const paid = Number(item.vendor_paid_total ?? 0);
  if (amount > 0 && paid >= amount) {
    return {
      key: 'paid',
      label: 'Paid',
      badge: 'success',
      hint: audience === 'vendor' ? 'Payment recorded on your account' : 'Nothing left to pay',
    };
  }

  if (audience === 'vendor') {
    if (item.vendor_completed_at || item.admin_completed_at) {
      return {
        key: 'awaiting_payment',
        label: 'Awaiting payment',
        badge: 'warning',
        hint: 'We will record payment soon',
      };
    }
    return {
      key: 'in_progress',
      label: 'In progress',
      badge: 'light',
      hint: 'Mark complete when the trip is finished',
    };
  }

  return {
    key: 'ready_to_pay',
    label: 'Ready to pay',
    badge: 'warning',
    hint: 'Record payment — one step',
  };
}

/** @deprecated use ready_to_pay — kept for filters */
export function isPayableTripStatus(key) {
  return key === 'ready_to_pay' || key === 'unpaid' || key === 'not_credited';
}

/** Vendor payout owed for this assignment (uses API-computed payout fields). */
export function vendorShareAmount(item = {}) {
  const direct = Number(item.vendor_amount ?? item.vendor_payout ?? item.partner_amount ?? item.amount ?? 0);
  if (direct > 0) return { value: direct, hint: null };

  const perPerson = Number(item.vendor_payout_per_person ?? 0);
  if (perPerson > 0) {
    const restCount = Number(item.rest_paid_people_count ?? 0);
    const minSize = Number(item.min_group_size ?? 0);
    const count = restCount > 0 ? restCount : (minSize > 0 ? minSize : 1);
    return { value: perPerson * count, hint: restCount > 0 ? null : 'est.' };
  }
  return { value: null, hint: null };
}

export function tripRemainingAmount(item = {}) {
  const share = vendorShareAmount(item).value ?? 0;
  const paid = Number(item.vendor_paid_total ?? 0);
  return Math.max(0, share - paid);
}

export function formatVendorShare(item = {}) {
  const { value, hint } = vendorShareAmount(item);
  if (value == null || value <= 0) return '—';
  const cc = (item.currency_code || 'NPR').toString().toUpperCase();
  const base = cc === 'USD'
    ? `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`
    : `Rs ${Math.round(value).toLocaleString()}`;
  return hint ? `${base} (${hint})` : base;
}

export function vendorTripDate(item = {}) {
  const raw = item.start_date || item.preferred_date || item.travel_date || item.created_at;
  if (!raw) return null;
  return String(raw).slice(0, 10);
}

export function vendorTripMonthKey(item = {}) {
  if (item.departure_month) return String(item.departure_month).slice(0, 7);
  const d = vendorTripDate(item);
  return d ? d.slice(0, 7) : null;
}

export function buildVendorTripRows(assignments = {}) {
  const { group_tours = [], shared_rentals = [], vendor_services = [] } = assignments;
  const rows = [];

  group_tours.forEach((t) => {
    rows.push(rowFromItem('Group', t, `/admin/group_tour_details/${t.idx}`, 'GroupTour', true));
  });

  shared_rentals.forEach((t) => {
    rows.push(rowFromItem('Shared', t, `/admin/shared_rental_details/${t.idx}`, 'SharedRental', true));
  });

  vendor_services.forEach((t) => {
    rows.push(rowFromItem('Private', t, null, 'VendorService', false));
  });

  return rows.sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
}

function rowFromItem(type, t, link, assignmentType, canViewMembers) {
  const share = vendorShareAmount(t);
  return {
    type,
    name: t.name || t.tour_name || t.from_to_display || t.idx,
    link,
    date: vendorTripDate(t),
    monthKey: vendorTripMonthKey(t),
    item: t,
    trip: { assignment_type: assignmentType, assignment_idx: t.idx, vendor_amount: share.value },
    canAdminMark: !!(t.vendor_id),
    canViewMembers,
  };
}

export function buildVendorMonthsForYear(rows, year, options = {}) {
  return MONTH_LABELS.map((label, i) => {
    const month = i + 1;
    const yearMonth = `${year}-${String(month).padStart(2, '0')}`;
    const monthRows = rows.filter((r) => r.monthKey === yearMonth);
    let paid = 0;
    let readyToPay = 0;
    monthRows.forEach((r) => {
      const key = vendorPayoutStatus(r.item, options).key;
      if (key === 'paid') paid += 1;
      else if (key !== 'cancelled') readyToPay += 1;
    });
    return { month, yearMonth, label, total: monthRows.length, paid, unpaid: readyToPay, notCredited: 0, owed: readyToPay };
  });
}

export function rowsForMonth(rows, yearMonth) {
  return rows.filter((r) => r.monthKey === yearMonth);
}

export function rowsWithoutMonth(rows) {
  return rows.filter((r) => !r.monthKey);
}
